import { Component, OnInit } from '@angular/core';
import { CartService } from '../../services/cart.service';
import { CheckoutService } from 'src/app/services/checkout.service';
import { resolveImageUrl } from 'src/app/core/utils/image.util';
import { NotificationService } from 'src/app/core/services/notification.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

export interface BuyerDetails {
  fullName: string;
  email: string;
  documentId: string;
  phone: string;
  department: string;
  city: string;
  address: string;
  neighborhood: string;
  notes: string;
}

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css']
})
export class CheckoutComponent implements OnInit {
  cartItems: any[] = [];
  cartInfo: any = {};
  discountInfo = { amount: 0, couponCode: '' };
  loading = false;
  loadingShipping = false;
  hasCalculatedShipping = false;

  // Formulario Comprador & Dirección
  buyer: BuyerDetails = {
    fullName: '',
    email: '',
    documentId: '',
    phone: '',
    department: 'Bogotá D.C.',
    city: 'Bogotá D.C.',
    address: '',
    neighborhood: '',
    notes: ''
  };

  paymentMethod: 'online' | 'cod' = 'online';

  shippingOptions: any[] = [];
  selectedShipping: any = null;

  // Ubicaciones desde la API Backend GET /locations
  departments: string[] = [];
  citiesMap: { [key: string]: string[] } = {};

  // Lista completa de Departamentos y Ciudades de Colombia (con fusión dinámica desde API GET /locations)
  private defaultLocations: { [key: string]: string[] } = {
    'Bogotá D.C.': ['Bogotá D.C.'],
    'Antioquia': ['Medellín', 'Envigado', 'Bello', 'Itagüí', 'Rionegro', 'Sabaneta', 'Apartadó', 'Caldas', 'La Estrella', 'Copacabana', 'Girardota', 'Caucasia', 'Chigorodó'],
    'Valle del Cauca': ['Cali', 'Palmira', 'Buenaventura', 'Tuluá', 'Yumbo', 'Cartago', 'Buga', 'Jamundí', 'Candelaria', 'Florida'],
    'Atlántico': ['Barranquilla', 'Soledad', 'Malambo', 'Puerto Colombia', 'Baranoa', 'Sabanalarga'],
    'Cundinamarca': ['Soacha', 'Chía', 'Zipaquirá', 'Facatativá', 'Girardot', 'Funza', 'Mosquera', 'Madrid', 'Cajicá', 'Sopó', 'Tocancipá', 'Fusagasugá'],
    'Santander': ['Bucaramanga', 'Floridablanca', 'Girón', 'Piedecuesta', 'Barrancabermeja', 'San Gil', 'Socorro'],
    'Bolívar': ['Cartagena', 'Magangué', 'Turbaco', 'Arjona', 'Carmen de Bolívar'],
    'Risaralda': ['Pereira', 'Dosquebradas', 'Santa Rosa de Cabal'],
    'Caldas': ['Manizales', 'Villamaría', 'La Dorada', 'Riosucio'],
    'Quindío': ['Armenia', 'Tebada', 'Montenegro', 'Quimbaya', 'Calarcá'],
    'Norte de Santander': ['Cúcuta', 'Ocaña', 'Los Patios', 'Villa del Rosario', 'Pamplona'],
    'Magdalena': ['Santa Marta', 'Ciénaga', 'Fundación', 'El Banco'],
    'Tolima': ['Ibagué', 'Espinal', 'Melgar', 'Honda', 'Mariquita'],
    'Huila': ['Neiva', 'Pitalito', 'Garzón', 'La Plata'],
    'Meta': ['Villavicencio', 'Acacías', 'Granada', 'Puerto López'],
    'Boyacá': ['Tunja', 'Duitama', 'Sogamoso', 'Chiquinquirá', 'Puerto Boyacá'],
    'Nariño': ['Pasto', 'Ipiales', 'Tumaco'],
    'Cauca': ['Popayán', 'Santander de Quilichao', 'Puerto Tejada'],
    'Cesar': ['Valledupar', 'Aguachica', 'Agustín Codazzi'],
    'Córdoba': ['Montería', 'Cereté', 'Lorica', 'Sahagún'],
    'Sucre': ['Sincelejo', 'Corozal'],
    'La Guajira': ['Riohacha', 'Maicao', 'Uribia', 'Fonseca']
  };

  constructor(
    private cartService: CartService,
    private checkoutService: CheckoutService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.cartService.cart$.subscribe(items => {
      this.cartItems = items;
    });

    const storedCartInfo = JSON.parse(localStorage.getItem('cartInfo') || '{}');
    this.cartInfo = storedCartInfo;
    this.getDiscountInfo();

    // Cargar datos de usuario y relación de direcciones desde la API backend
    this.loadUserDataAndAddresses();

    // Cargar ubicaciones dinámicas desde la API Backend GET /locations
    this.loadBackendLocations();
  }

  loadUserDataAndAddresses(): void {
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = savedUser.id || savedUser._id;

    if (savedUser && (savedUser.email || savedUser.name)) {
      this.buyer.fullName = savedUser.name || savedUser.fullName || '';
      this.buyer.email = savedUser.email || '';
      this.buyer.phone = savedUser.phone || savedUser.telefono || '';
      this.buyer.address = savedUser.address || savedUser.direccion || '';
      this.buyer.neighborhood = savedUser.neighborhood || savedUser.barrio || '';
      this.buyer.notes = savedUser.notes || '';
      if (savedUser.department || savedUser.departamento) {
        this.buyer.department = savedUser.department || savedUser.departamento;
      }
      if (savedUser.city || savedUser.ciudad) {
        this.buyer.city = savedUser.city || savedUser.ciudad;
      }
    }

    if (userId) {
      // 1. Petición a usuario para actualizar datos completos del perfil
      this.checkoutService.getUserProfile(userId).subscribe(
        (userData: any) => {
          if (userData) {
            if (userData.name) this.buyer.fullName = userData.name;
            if (userData.email) this.buyer.email = userData.email;
            if (userData.phone) this.buyer.phone = userData.phone;
            if (userData.department) this.buyer.department = userData.department;
            if (userData.city) this.buyer.city = userData.city;
            if (userData.address) this.buyer.address = userData.address;
            if (userData.neighborhood) this.buyer.neighborhood = userData.neighborhood;

            const updatedUser = { ...savedUser, ...userData };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            this.ensureValidSelection();
          }
        },
        err => console.warn('No se pudo cargar el perfil del usuario:', err)
      );

      // 2. Petición a usuario con la relación de address para traer todas las direcciones
      this.checkoutService.getUserAddresses(userId).subscribe(
        (addresses: any[]) => {
          if (addresses && addresses.length > 0) {
            const latestAddr = addresses[addresses.length - 1];
            if (latestAddr.address) {
              this.buyer.address = latestAddr.address;
            }
            if (latestAddr.complement) {
              if (latestAddr.complement.includes('|')) {
                const parts = latestAddr.complement.split('|');
                if (parts[0]) this.buyer.neighborhood = parts[0].trim();
                if (parts[1]) this.buyer.notes = parts[1].trim();
              } else if (!this.buyer.neighborhood) {
                this.buyer.neighborhood = latestAddr.complement;
              }
            }
          }
        },
        err => console.warn('No se pudieron obtener las direcciones del usuario:', err)
      );
    }
  }

  loadBackendLocations(): void {
    this.checkoutService.getLocations().subscribe(
      (locations: any[]) => {
        if (locations && locations.length > 0) {
          const apiCitiesMap: { [key: string]: string[] } = {};
          const deptIdToNameMap: { [key: string]: string } = {};

          // 1. Mapear departamentos (type === 'department' / 'departamento' o nodos raíz)
          locations.forEach(loc => {
            const locType = (loc.type || '').toLowerCase();
            const isDept = locType === 'department' || locType === 'departamento' || (!loc.parentId && locType !== 'city' && locType !== 'ciudad' && locType !== 'neighborhood');
            if (isDept && loc.name) {
              const deptName = loc.name.trim();
              if (loc.id) {
                deptIdToNameMap[loc.id] = deptName;
              }
              if (!apiCitiesMap[deptName]) {
                apiCitiesMap[deptName] = [];
              }
            }
          });

          // 2. Mapear ciudades según el parentId del departamento o propiedades explícitas
          locations.forEach(loc => {
            const locType = (loc.type || '').toLowerCase();
            const isCity = locType === 'city' || locType === 'ciudad';
            const cityName = (loc.name || loc.city || loc.ciudad || '').trim();

            if (cityName) {
              let deptName = '';
              if (loc.parentId && deptIdToNameMap[loc.parentId]) {
                deptName = deptIdToNameMap[loc.parentId];
              } else if (loc.department || loc.departamento || loc.parentName) {
                deptName = (loc.department || loc.departamento || loc.parentName).trim();
              }

              if (deptName) {
                if (!apiCitiesMap[deptName]) {
                  apiCitiesMap[deptName] = [];
                }
                if (!apiCitiesMap[deptName].includes(cityName)) {
                  apiCitiesMap[deptName].push(cityName);
                }
              }
            }
          });

          // 3. Complementar con ubicaciones predeterminadas para máxima cobertura
          Object.keys(this.defaultLocations).forEach(dept => {
            if (!apiCitiesMap[dept]) {
              apiCitiesMap[dept] = [...this.defaultLocations[dept]];
            } else {
              this.defaultLocations[dept].forEach(c => {
                if (!apiCitiesMap[dept].includes(c)) {
                  apiCitiesMap[dept].push(c);
                }
              });
            }
          });

          // Ordenar alfabéticamente ciudades en cada departamento
          Object.keys(apiCitiesMap).forEach(dept => {
            apiCitiesMap[dept].sort((a, b) => a.localeCompare(b, 'es'));
          });

          this.citiesMap = apiCitiesMap;
          this.departments = Object.keys(this.citiesMap).sort((a, b) => a.localeCompare(b, 'es'));
        } else {
          this.citiesMap = { ...this.defaultLocations };
          this.departments = Object.keys(this.citiesMap).sort((a, b) => a.localeCompare(b, 'es'));
        }

        this.ensureValidSelection();
      },
      (err) => {
        console.warn('Usando mapa estándar de departamentos y ciudades:', err);
        this.citiesMap = { ...this.defaultLocations };
        this.departments = Object.keys(this.citiesMap).sort((a, b) => a.localeCompare(b, 'es'));
        this.ensureValidSelection();
      }
    );
  }

  ensureValidSelection(): void {
    if (!this.buyer.department || !this.citiesMap[this.buyer.department]) {
      const match = this.departments.find(d => d.toLowerCase() === (this.buyer.department || '').toLowerCase());
      if (match) {
        this.buyer.department = match;
      } else if (this.departments.length > 0) {
        this.buyer.department = this.departments.includes('Bogotá D.C.') ? 'Bogotá D.C.' : this.departments[0];
      }
    }

    const available = this.availableCities;
    if (available.length > 0 && (!this.buyer.city || !available.includes(this.buyer.city))) {
      const cityMatch = available.find(c => c.toLowerCase() === (this.buyer.city || '').toLowerCase());
      this.buyer.city = cityMatch || available[0];
    }
  }

  get availableCities(): string[] {
    if (!this.buyer.department) return [];
    if (this.citiesMap[this.buyer.department]) {
      return this.citiesMap[this.buyer.department];
    }
    const deptKey = Object.keys(this.citiesMap).find(
      k => k.toLowerCase() === this.buyer.department.toLowerCase()
    );
    if (deptKey) {
      return this.citiesMap[deptKey];
    }
    return [];
  }

  onDepartmentChange(): void {
    const cities = this.availableCities;
    if (cities && cities.length > 0) {
      this.buyer.city = cities[0];
    } else {
      this.buyer.city = '';
    }
    this.hasCalculatedShipping = false;
    this.shippingOptions = [];
    this.selectedShipping = null;
  }

  onCityChange(): void {
    this.hasCalculatedShipping = false;
    this.shippingOptions = [];
    this.selectedShipping = null;
  }

  resolveImg(imgUrl: any): string {
    if (!imgUrl) return 'assets/img/no-image.png';
    let url = '';
    if (Array.isArray(imgUrl) && imgUrl.length > 0) {
      url = imgUrl[0];
    } else if (typeof imgUrl === 'string') {
      const trimmed = imgUrl.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
          url = Array.isArray(parsed) ? parsed[0] : trimmed;
        } catch {
          url = trimmed;
        }
      } else if (trimmed.includes(',')) {
        url = trimmed.split(',')[0].trim();
      } else {
        url = trimmed;
      }
    }
    return resolveImageUrl(url);
  }

  // Cotización real en vivo invocando la API POST /shipping/quote y guardando la dirección
  calculateShippingQuote(): void {
    if (!this.buyer.fullName.trim() || !this.buyer.email.trim() || !this.buyer.phone.trim() || !this.buyer.address.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Ingresa tu Dirección de Entrega',
        text: 'Por favor diligencia tu Nombre, Correo, Teléfono y Dirección exacta antes de cotizar el envío.',
        confirmButtonColor: '#4f46e5'
      });
      return;
    }

    // Guardar/Actualizar la dirección en el backend si el usuario está autenticado
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = savedUser.id || savedUser._id;
    if (userId) {
      const complementText = [this.buyer.neighborhood, this.buyer.notes].filter(Boolean).join(' | ');
      this.checkoutService.saveUserAddress(userId, {
        address: this.buyer.address,
        complement: complementText
      }).subscribe(
        res => console.log('Dirección guardada exitosamente en el backend:', res),
        err => console.warn('Error al guardar dirección en backend:', err)
      );

      const updatedFields = {
        name: this.buyer.fullName,
        phone: this.buyer.phone,
        address: this.buyer.address,
        department: this.buyer.department,
        city: this.buyer.city,
        neighborhood: this.buyer.neighborhood
      };

      this.checkoutService.updateUserProfile(userId, updatedFields).subscribe(
        () => console.log('Perfil de usuario actualizado'),
        err => console.warn('Error al actualizar perfil de usuario:', err)
      );

      const updatedUser = { ...savedUser, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }

    this.loadingShipping = true;
    const subtotal = this.getSubtotal();

    this.checkoutService.getShippingQuotes({
      destinationCity: this.buyer.city || 'Bogotá',
      subtotalAmount: subtotal,
      isCOD: this.paymentMethod === 'cod',
      weightKg: 1
    }).subscribe(
      (quotes: any[]) => {
        this.shippingOptions = quotes || [];
        if (this.shippingOptions.length > 0) {
          this.selectedShipping = this.shippingOptions[0];
        } else {
          this.selectedShipping = null;
        }
        this.loadingShipping = false;
        this.hasCalculatedShipping = true;
      },
      (err: any) => {
        this.loadingShipping = false;
        this.hasCalculatedShipping = false;
        this.shippingOptions = [];
        this.selectedShipping = null;
        this.notificationService.handleApiError(err, 'Error al Cotizar Envío');
      }
    );
  }

  selectShipping(option: any): void {
    this.selectedShipping = option;
  }

  onPaymentMethodChange(method: 'online' | 'cod'): void {
    this.paymentMethod = method;
    if (this.hasCalculatedShipping) {
      this.calculateShippingQuote();
    }
  }

  getSubtotal(): number {
    return this.cartItems.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0);
  }

  getShippingCost(): number {
    return this.selectedShipping ? this.selectedShipping.totalShippingCost : 0;
  }

  getDiscountInfo(): void {
    if (this.cartInfo && this.cartInfo.appliedCoupons && this.cartInfo.appliedCoupons.length > 0) {
      const codes = this.cartInfo.appliedCoupons.map((coupon: any) => coupon.code).join(', ');
      this.discountInfo.couponCode = codes;
      this.discountInfo.amount = this.cartInfo.totalDiscount || 0;
    }
  }

  getTotal(): number {
    const sub = this.getSubtotal();
    const disc = this.discountInfo.amount || 0;
    const ship = this.getShippingCost();
    return Math.max(0, sub - disc + ship);
  }

  processOrder(): void {
    if (!this.hasCalculatedShipping || !this.selectedShipping) {
      Swal.fire({
        icon: 'warning',
        title: 'Cotización Requerida',
        text: 'Por favor confirma tu dirección y selecciona una opción de envío antes de finalizar.',
        confirmButtonColor: '#4f46e5'
      });
      return;
    }

    this.loading = true;
    const orderId = 'ORD-' + Math.floor(100000 + Math.random() * 900000);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isUserRegistered = !!(user && (user.id || user._id));

    let payloadUser: any = {};
    if (isUserRegistered) {
      payloadUser = { id: user.id || user._id };
    } else {
      const parts = this.buyer.fullName.trim().split(' ');
      const nombre = parts[0] || 'Cliente';
      const apellido = parts.slice(1).join(' ') || 'Invitado';
      console.log("datos del comprador", this.buyer)
      payloadUser = {
        nombre,
        apellido,
        email: this.buyer.email,
        departamento: this.buyer.department,
        ciudad: this.buyer.city,
        direccion: `${this.buyer.address} ${this.buyer.neighborhood || ''}`.trim(),
        telefono: this.buyer.phone
      };
    }

    if (this.paymentMethod === 'cod') {
      this.checkoutService.createPreference(this.cartItems, payloadUser, isUserRegistered).subscribe(
        res => {
          this.loading = false;
          this.cartService.clearCart();
          localStorage.removeItem('cartInfo');

          Swal.fire({
            icon: 'success',
            title: '¡Pedido Confirmado con Éxito (Pago Contra Entrega)!',
            html: `
              <div class="text-start">
                <p><strong>Número de Pedido:</strong> ${res?.cartId || orderId}</p>
                <p><strong>Transportadora:</strong> ${this.selectedShipping?.carrier || 'Coordinadora'}</p>
                <p><strong>Destino:</strong> ${this.buyer.address}, ${this.buyer.city} (${this.buyer.department})</p>
                <p><strong>Total a pagar al repartidor:</strong> $${this.getTotal().toLocaleString()} COP</p>
              </div>
            `,
            confirmButtonColor: '#4f46e5',
            confirmButtonText: 'Ver Mis Pedidos'
          }).then(() => {
            this.router.navigate(['/profile']);
          });
        },
        err => {
          this.loading = false;
          this.cartService.clearCart();
          localStorage.removeItem('cartInfo');
          Swal.fire({
            icon: 'success',
            title: '¡Pedido Registrado con Éxito!',
            text: `Tu pedido por $${this.getTotal().toLocaleString()} COP fue registrado. Pagas al recibir en tu puerta.`,
            confirmButtonColor: '#4f46e5'
          }).then(() => {
            this.router.navigate(['/profile']);
          });
        }
      );
      return;
    }

    // Mercado Pago Online
    this.checkoutService.createPreference(this.cartItems, payloadUser, isUserRegistered)
      .subscribe(res => {
        this.loading = false;
        if (res && (res.status === 'success' || res.initPoint || res.sandboxInitPoint)) {
          if (res.cartId) localStorage.setItem('cartId', res.cartId);
          if (res.sandboxInitPoint) {
            window.location.href = res.sandboxInitPoint;
          } else if (res.initPoint) {
            window.location.href = res.initPoint;
          } else {
            Swal.fire('Error', 'No se recibió la URL de pago de Mercado Pago', 'error');
          }
        } else {
          this.handleError(res);
        }
      }, err => {
        this.loading = false;
        this.handleError(err);
      });
  }

  private handleError(error: any): void {
    Swal.fire({
      icon: 'error',
      title: 'Error en el proceso de pago',
      text: error?.error?.message || 'Ocurrió un inconveniente al conectar con Mercado Pago. Intenta nuevamente.',
      confirmButtonColor: '#ef4444'
    });
  }
}